import React, { useEffect, useState, useMemo } from 'react';

import { useSnackbar } from 'notistack';
import MUIDataTable from 'mui-datatables';
import cx from 'clsx';
import AddIcon from '@material-ui/icons/Add';
import { useHistory } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/react-hooks';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import {
  IconButton,
  Tooltip,
  Typography,
  TableCell,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@material-ui/core';

import CustomOptions from 'src/components/CustomOptions';
import {
  edgeToList,
  errorMessage,
  successMessage,
  getOptions,
  generateColumn,
} from 'src/utils/commonFunctions';
import LoadingIndicator from '../../LoadingIndicator';
import {
  LIST_EMPLOYEES,
  LIST_S3FILES,
  LIST_RAZON_SOCIAL,
  UPDATE_EMPLOYEE,
} from './queries';
import { FILE_DOWNLOAD_COMMON } from '../EmployeeFiles/queries';
import { textLabels } from '../../../translations/components/MUIDataTable';

import useStyles from './styles';
import PDFViewerModal from 'src/components/PDFViewerModal';
import CustomColumnHeader from '../../TableComponents/CustomColumnHeader';
import PageviewOutlinedIcon from '@material-ui/icons/PageviewOutlined';
import CloudDownloadIcon from '@material-ui/icons/CloudDownload';
import { saveAs } from 'file-saver';

function Employee(props) {
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();
  const [showModal, setShowModal] = useState(false);
  const [isDownload, setIsDownload] = useState(false);
  const [url, setUrl] = useState('');
  const [filesList, setFilesList] = useState([]);
  const [typeList, setTypeList] = useState([]);
  const [filterRazon, setFilterRazon] = useState('');
  const [filterFirmada, setFilterFirmada] = useState('');
  const [filterActivos, setFilterActivos] = useState('');

  const {
    loading: TypesLoading,
    error: TypesError,
    data: TypesData,
  } = useQuery(LIST_RAZON_SOCIAL);
  const {
    loading: listLoading,
    error: listError,
    data: listData,
  } = useQuery(LIST_EMPLOYEES, { fetchPolicy: 'network-only' });
  const {
    loading: fileLoading,
    error: error,
    data: data,
  } = useQuery(LIST_S3FILES, { fetchPolicy: 'network-only' });

  const [downloadFile, { loadingFile, dataFile }] = useMutation(
    FILE_DOWNLOAD_COMMON,
    {
      onError(error) {
        errorMessage(error, enqueueSnackbar);
      },
      onCompleted(data) {
        if (!!data && !isDownload) {
          setUrl(data?.fileDownloadCommon?.url);
          setShowModal(true);
        } else {
          setShowModal(false);
          saveAs(
            data?.fileDownloadCommon?.url,
            data?.fileDownloadCommon?.fileName,
          );
          successMessage('Archivo descargado.', enqueueSnackbar);
        }
      },
    },
  );

  const [updateEmployeeState] = useMutation(
    UPDATE_EMPLOYEE,
    getOptions({
      mutationName: 'updateUser',
      modelName: 'user',
      message: 'Estado actualizado con éxito.',
      enqueueSnackbar,
      refetchQueries: [{ query: LIST_EMPLOYEES }, { query: LIST_S3FILES }],
    }),
  );

  const changeState = (id, enabled) => {
    updateEmployeeState({
      variables: {
        id,
        input: {
          isActive: !enabled,
        },
      },
    });
  };

  const getUrl = (id, isDownload) => {
    downloadFile({
      variables: { id, isDownload },
    });
  };

  const employeeList = useMemo(() => {
    if (!listData || !filesList) return [];

    const rawEmployees = edgeToList(listData, 'listEmployees');

    return rawEmployees.map(emp => {
      const file = filesList.find(f => f?.client?.id == emp.id);
      return {
        name: emp.user?.firstName,
        lastName: emp.user?.lastName,
        maternalSurname: emp.user?.maternalSurname,
        state: emp.state,
        occupation: emp.occupation,
        razonSocial: emp.razonSocial,
        active: {
          id: emp.user?.id,
          active: emp.user?.isActive,
        },
        options: {
          clientId: emp.id,
          fileId: file?.id || '',
        },
      };
    });
  }, [listData, filesList]);

  const filteredEmployees = employeeList.filter(item => {
    if (filterRazon && item?.razonSocial !== filterRazon) return false;

    if (
      filterFirmada !== '' &&
      (item?.state === 'employee_completed') !== (filterFirmada === 'true')
    )
      return false;

    if (
      filterActivos !== '' &&
      item?.active?.active !== (filterActivos === 'true')
    )
      return false;

    return true;
  });

  useEffect(() => {
    if (!!data) setFilesList(edgeToList(data, 'listEmployeeSheets'));
  }, [data]);

  useEffect(() => {
    if (!!TypesData) setTypeList(TypesData.razonSocialChoices);
  }, [TypesData]);

  const columns = [
    generateColumn('Nombres', 'name'),
    generateColumn('Apellidos Paterno', 'lastName'),
    generateColumn('Apellidos Materno', 'maternalSurname'),
    generateColumn('Cargo', 'occupation'),
    {
      name: 'razonSocial',
      label: 'Razón Social',
      options: {
        sort: false,
        customHeadRender: (columnMeta, updateDirection) => {
          return (
            <CustomColumnHeader
              id={columnMeta.index}
              key={columnMeta.index}
              columnMeta={columnMeta}
              updateDirection={updateDirection}
            />
          );
        },
        customBodyRender: value => {
          return typeList?.find(i => i.value == value)?.name;
        },
      },
    },
    {
      name: 'state',
      label: 'Ficha Firmada',
      options: {
        sort: false,
        customBodyRender: (value, row) => {
          return (
            <Button
              variant="contained"
              size="small"
              className={cx(
                classes.stateButton,
                value == 'employee_completed' ? classes.orange : classes.gray,
              )}
            >
              <CheckCircleIcon />
            </Button>
          );
        },
        customHeadRender: columnMeta => {
          return (
            <TableCell key={0} style={{ borderBottom: 'none', width: '10px' }}>
              <Typography
                variant="subtitle1"
                align="left"
                className={classes.tableTitle}
              >
                {columnMeta.label}
              </Typography>
            </TableCell>
          );
        },
      },
    },
    {
      name: 'active',
      label: 'Activo',
      options: {
        sort: false,
        customBodyRender: (value, row) => {
          return (
            <Tooltip
              title={value.active ? 'Desactivar' : 'Activar'}
              placement="top"
            >
              <Button
                variant="contained"
                size="small"
                className={cx(
                  classes.stateButton,
                  value.active ? classes.orange : classes.gray,
                )}
                onClick={$ev => {
                  $ev.stopPropagation();
                  changeState(value.id, value.active);
                }}
              >
                <CheckCircleIcon />
              </Button>
            </Tooltip>
          );
        },
        customHeadRender: columnMeta => {
          return (
            <TableCell key={0} style={{ borderBottom: 'none', width: '10px' }}>
              <Typography
                variant="subtitle1"
                align="left"
                className={classes.tableTitle}
              >
                {columnMeta.label}
              </Typography>
            </TableCell>
          );
        },
      },
    },
    {
      name: 'options',
      label: 'Opciones',
      options: {
        sort: false,
        customBodyRender: value => {
          const employee = value.clientId;
          const file = value.fileId;
          let options = [
            'oedit',
            file && 'oadditional',
            file && 'osecondadditional',
          ];
          return (
            <CustomOptions
              id={employee}
              model="Colaborador"
              options={options}
              additionalOption={
                <span>
                  <Tooltip title="Visualizar" placement="top" arrow>
                    <IconButton
                      variant="contained"
                      color="secondary"
                      size="small"
                      onClick={$ev => {
                        $ev.stopPropagation();
                        setIsDownload(false);
                        getUrl(file, false);
                      }}
                      className={classes.optionButtons}
                    >
                      <PageviewOutlinedIcon className={classes.optionIcon} />
                    </IconButton>
                  </Tooltip>
                </span>
              }
              secondAdditionalOption={
                <span>
                  <Tooltip title="Descargar" placement="top" arrow>
                    <IconButton
                      variant="contained"
                      color="secondary"
                      size="small"
                      onClick={$ev => {
                        $ev.stopPropagation();
                        setIsDownload(true);
                        getUrl(file, true);
                      }}
                      className={classes.optionButtons}
                    >
                      <CloudDownloadIcon className={classes.optionIcon} />
                    </IconButton>
                  </Tooltip>
                </span>
              }
            />
          );
        },
        customHeadRender: columnMeta => {
          return (
            <TableCell key={7} style={{ borderBottom: 'none' }}>
              <Typography
                variant="subtitle1"
                align="left"
                className={classes.tableTitle}
              >
                {columnMeta.label}
              </Typography>
            </TableCell>
          );
        },
      },
    },
  ];

  const options = {
    filterType: 'dropdown',
    responsive: 'standard',
    selectableRows: 'none',
    print: false,
    filter: false,
    download: false,
    textLabels,
    customToolbar: () => (
      <Tooltip title="Nuevo Colaborador">
        <IconButton
          color="secondary"
          onClick={() => history.push(`${history.location.pathname}/crear`)}
        >
          <AddIcon />
        </IconButton>
      </Tooltip>
    ),
  };

  return (
    <>
      <div style={{ display: 'flex', gap: 20, marginBottom: 20 }}>
        <FormControl variant="outlined" style={{ minWidth: 250 }}>
          <InputLabel>Razón Social</InputLabel>
          <Select
            value={filterRazon}
            onChange={e => setFilterRazon(e.target.value)}
            label="Razón Social"
          >
            <MenuItem value="">
              <em>Todas</em>
            </MenuItem>
            {typeList.map(t => (
              <MenuItem key={t.value} value={t.value}>
                {t.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl variant="outlined" style={{ minWidth: 250 }}>
          <InputLabel>Ficha Firmada</InputLabel>
          <Select
            value={filterFirmada}
            onChange={e => setFilterFirmada(e.target.value)}
            label="Ficha Firmada"
          >
            <MenuItem value="">
              <em>Todas</em>
            </MenuItem>
            <MenuItem value="true">Firmada</MenuItem>
            <MenuItem value="false">No Firmada</MenuItem>
          </Select>
        </FormControl>
        <FormControl variant="outlined" style={{ minWidth: 200 }}>
          <InputLabel>Activo</InputLabel>
          <Select
            value={filterActivos}
            onChange={e => setFilterActivos(e.target.value)}
            label="Activo"
          >
            <MenuItem value="">
              <em>Todos</em>
            </MenuItem>
            <MenuItem value="true">Activos</MenuItem>
            <MenuItem value="false">Inactivos</MenuItem>
          </Select>
        </FormControl>
      </div>

      {!listLoading || !fileLoading ? (
        <MUIDataTable
          title="Colaboradores"
          data={filteredEmployees}
          columns={columns}
          options={options}
        />
      ) : (
        <LoadingIndicator />
      )}

      <PDFViewerModal
        showModal={showModal}
        onClosePress={() => setShowModal(false)}
        fileURL={url}
        loading={loadingFile}
      />
    </>
  );
}

export default Employee;
